import React, { useContext } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { PageAbsolutePath } from '../../../../consts/Common';
import Table, { HeaderRow, TableRow } from '../../../common/Widgets/Table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import List from '../../../common/Widgets/List';
import Image from '../../../common/Widgets/Image';
import ButtonTabs, { QuoteButtonTab } from '../../../common/Widgets/ButtonTabs';
import { SiteContext } from '../../../../context/SiteContext';
import {
  InsuranceJourneyContext,
  InsuranceJourneyState
} from '../../../common/CommonTypes';
import useCarInsuranceController from '../../../../hooks/insuranceJourney/useCarInsuranceController';
import { getStartQuotePath } from '../InsuranceMethods';
import { useErrorBoundary } from 'react-error-boundary';

const PolestarLandingPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();

  const { site } = useContext(SiteContext);

  const { pageEvents } = useOutletContext<InsuranceJourneyContext>();

  const { commonEvents } = pageEvents;

  const { setQuote } = commonEvents;

  const { getNewQuote } = useCarInsuranceController();

  const getNewQuoteSuccess = (quote: InsuranceJourneyState) => {
    setQuote({
      ...quote,
      tiaProductCode: site.brand.tiaProductCode
    });

    navigate(PageAbsolutePath.Insurance.AboutYou);
  };

  const getNewQuoteAlternateSuccess = (quote: InsuranceJourneyState) => {
    setQuote({
      ...quote,
      tiaProductCode: site.brand.tiaProductCode
    });

    navigate(
      getStartQuotePath(
        site.user.isDealer ?? false,
        site.brand.useAlternateEligibility
      )
    );
  };

  const onBackendFailure = (err: string) => {
    showBoundary(new Error(err));
  };

  const getStarted = () => {
    getNewQuote(
      site.brand.brandName,
      site.user?.insuranceDealerCode ?? '',
      site.user.token,
      getNewQuoteSuccess,
      onBackendFailure
    );
  };

  const getStartedAlternate = () => {
    getNewQuote(
      site.brand.brandName,
      site.user?.insuranceDealerCode ?? '',
      site.user.token,
      getNewQuoteAlternateSuccess,
      onBackendFailure
    );
  };

  const getRetrieveQuote = () => {
    navigate(PageAbsolutePath.Insurance.RetrieveQuote);
  };

  const polestarTableHeader: HeaderRow = {
    columns: ['Polestar Guaranteed', 'Polestar Insurance', 'Top UK insurers']
  };

  const tickComponent = <FontAwesomeIcon icon={faCheck} />;

  const crossComponent = <FontAwesomeIcon icon={faXmark} />;

  const polestarTableRows: TableRow[] = [
    { columns: ['Polestar genuine parts', tickComponent, crossComponent] },
    {
      columns: ['Polestar approved repairers', tickComponent, crossComponent]
    },
    {
      columns: ['Polestar trained technicians', tickComponent, crossComponent]
    },
    {
      columns: [
        'Polestar warranty and safety rating preserved by only fitting Polestar genuine parts',
        tickComponent,
        crossComponent
      ]
    }
  ];

  const standardCoverItems: string[] = [
    'Cover for your charging cable',
    'Battery cover against accidental damage or theft*',
    'Comprehensive cover',
    'Courtesy car (subject to availability)',
    '24-hour accident recovery',
    'Comprehensive EU cover',
    'Replacement lock cover',
    '5 year repair guarantee',
    'Personal accident cover',
    'Personal possessions cover',
    'A brand new Polestar if yours is written off or stolen within 12 months of its new registration date'
  ];

  const extraCoverItems: string[] = [
    '£100,000 legal expenses',
    'No-claim discount protection',
    'Upgrade your courtesy car and stay mobile if your car is written-off or stolen',
    'Lost or stolen key cover up to £1,000'
  ];

  const buttonTabs: QuoteButtonTab[] = [
    {
      buttonText: 'Already own your Polestar?',
      buttonClass: 'btn-start',
      description:
        'Say goodbye to normal with comprehensive cover tailored for your Polestar.Get a competitive 12 month quote today.',
      getQuoteText: 'Start Quote',
      onGetQuoteClick: getStarted,
      retrieveQuoteText: 'Retrieve Quote',
      onRetrieveQuoteClick: getRetrieveQuote
    },
    {
      buttonText: 'Awaiting delivery of your Polestar?',
      buttonClass: 'btn-retrieve',
      description: `To help you get off to the best start, we give you 7 days
                    complimentary Driveaway Insurance when you buy a new
                    Polestar.`,
      getQuoteText: 'Start Quote',
      onGetQuoteClick: getStartedAlternate,
      retrieveQuoteText: 'Retrieve Quote',
      onRetrieveQuoteClick: getRetrieveQuote
    }
  ];

  return (
    <div className='insurance-landing-page primary-section'>
      <div className='hero-image'>
        <div className='image-text'>
          <h1 className='main-header'>
            Give your Polestar the cover it deserves
          </h1>
          <ButtonTabs id='hero-buttons' tabs={buttonTabs} />
        </div>
      </div>
      <div className='flex-row grey justify-center secondary-section'>
        <div className='margin-panel'>
          <p className='second-font'>
            With Polestar Insurance, you can be confident that your car will
            receive the specialist electric vehicle cover and care it needs. You
            can manage your policy at the touch of a button and pay monthly,
            keeping things easy for you.
          </p>
          <p className='second-font'>
            Our exclusive repair benefits guarantee what many insurers don't.
          </p>
          <Table
            id='landing-page-table'
            header={polestarTableHeader}
            rows={polestarTableRows}
            addendum='Source: Research of UK motor insurer policy wordings, August 2024.'
          />
        </div>
      </div>
      <div className='tertiary-section'>
        <div className='flex-row'>
          <div className='two-columns cover-list-column'>
            <List
              id='standard-cover'
              title='Standard cover'
              listItems={standardCoverItems}
            />
            <p className='list-addendum'>
              *Exclusions apply for leased batteries
            </p>
          </div>
          <div className='two-columns flex-column'>
            <List
              id='extra-cover'
              title='Optional extra cover'
              listItems={extraCoverItems}
            />
            <Image
              id='optional-extra-image'
              source='./brands/polestar/polestar_list_image.jpg'
              altText='list-image-1'
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolestarLandingPage;
